import { useState } from "react";
import { ArrowRightLeft, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { tenantColorVars } from "@/lib/messages/merged-thread-colors";
import { sourceSubtitle } from "@/lib/messages/merged-threads";
import { cn } from "@/lib/utils";

/**
 * The merged WhatsApp view's source filter — the header pill that reads "All sources" (or
 * "3 of 7 sources" once narrowed) and opens a checkbox list of every clinic number the patient has
 * a thread with. Unchecking a clinic drops its messages out of the merged stream and out of the
 * reply-from picker; at least one clinic always stays checked so the conversation can never go
 * blank (the last remaining one is disabled rather than hidden, so the state is self-explanatory).
 *
 * @param {object} props
 * @param {Array<{ threadId: string, botId: string, shortName: string, label: string, lastActivityAt: string|null, visible: boolean }>} props.sources
 *   Every source for this patient, each flagged with its current visibility.
 * @param {number} props.visibleCount
 * @param {boolean} props.allVisible
 * @param {(botId: string) => void} props.onToggle
 * @param {() => void} props.onShowAll
 */
export function MergedSourcesFilter({ sources, visibleCount, allVisible, onToggle, onShowAll }) {
  const [open, setOpen] = useState(false);
  const total = Array.isArray(sources) ? sources.length : 0;
  if (total === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-5 items-center gap-1.5 rounded-full border border-primary/35 bg-primary/[0.07] px-2.5 text-[10.5px] font-semibold leading-none text-primary transition hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title="Choose which clinic numbers appear in this merged conversation."
          aria-label={
            allVisible
              ? `All ${total} clinic sources shown — choose which to show`
              : `${visibleCount} of ${total} clinic sources shown — choose which to show`
          }
        >
          <ArrowRightLeft className="h-3 w-3" strokeWidth={2.4} aria-hidden />
          {allVisible ? "All sources" : `${visibleCount} of ${total} sources`}
          <ChevronDown className="h-2.5 w-2.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-72 p-1.5">
        <div className="flex items-center justify-between gap-2 px-2 py-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Show clinics
          </p>
          {!allVisible ? (
            <button
              type="button"
              onClick={onShowAll}
              className="text-[10px] font-medium text-primary transition hover:underline"
            >
              Show all
            </button>
          ) : null}
        </div>
        <ScrollArea viewportClassName="max-h-64">
          <div className="space-y-0.5">
            {sources.map((source) => {
              // The last visible clinic can't be unchecked — something must stay on screen.
              const isLastVisible = source.visible && visibleCount <= 1;
              const subtitle = sourceSubtitle(source);
              return (
                <label
                  key={source.threadId}
                  style={tenantColorVars(source.botId)}
                  title={isLastVisible ? "At least one clinic must stay visible" : source.label}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 transition",
                    isLastVisible ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-muted",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={source.visible}
                    disabled={isLastVisible}
                    onChange={() => onToggle?.(source.botId)}
                    aria-label={`Show ${source.shortName}`}
                    className="size-3.5 shrink-0 rounded border-border accent-primary disabled:cursor-not-allowed"
                  />
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-(--seg-solid)" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {source.shortName}
                    </span>
                    {subtitle ? (
                      <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
